import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils';
import { setupScene, animateScene, centerAndFitModel } from '../utils/sceneSetup';

function ReplicadViewer({ stepUrl }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsRef = useRef(null);
  const meshGroupRef = useRef(null);
  const workerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('Initializing CAD engine...');
  const [error, setError] = useState(null);

  // Initialize web worker
  useEffect(() => {
    const worker = new Worker(
      new URL('../workers/cadWorker.js', import.meta.url)
    );

    worker.onmessage = (e) => {
      const { type, data } = e.data;

      if (type === 'ready') {
        console.log('CAD worker ready');
        if (workerRef.initTimeoutRef) clearTimeout(workerRef.initTimeoutRef.current);
        setLoadingStatus('CAD engine ready');
      } else if (type === 'loaded') {
        renderGeometry(data.faces, data.edges);
        setLoading(false);
      } else if (type === 'error') {
        console.error('CAD worker error:', data.message);
        setError(data.message);
        setLoading(false);
      }
    };

    worker.onerror = (e) => {
      console.error('Worker error:', e);
      setError(`Worker failed: ${e.message || 'Unknown error'}`);
      setLoading(false);
    };

    worker.postMessage({ type: 'init' });
    workerRef.current = worker;

    // Timeout fallback — if the worker never sends 'ready', show an error
    const initTimeout = setTimeout(() => {
      setError('CAD engine failed to initialize (timeout). Check browser console for details.');
      setLoading(false);
    }, 15000);
    const initTimeoutRef = { current: initTimeout };
    workerRef.initTimeoutRef = initTimeoutRef;

    return () => {
      clearTimeout(initTimeoutRef.current);
      worker.terminate();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Setup Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    const { scene, camera, renderer, controls } = setupScene(mountRef.current);
    sceneRef.current = scene;
    controlsRef.current = controls;

    animateScene(renderer, scene, camera, controls);

    const handleResize = () => {
      if (!mountRef.current) return;
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      const mount = mountRef.current;
      if (mount && renderer.domElement) mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  // Load STEP file when URL changes
  useEffect(() => {
    if (!stepUrl || !workerRef.current) return;

    const loadSTEP = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadingStatus('Fetching STEP file...');

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const password = sessionStorage.getItem('access_password');
        const headers = password ? { 'X-Access-Password': password } : {};
        const response = await fetch(`${backendUrl}${stepUrl}`, { headers });

        if (response.status === 401) {
          sessionStorage.removeItem('access_password');
          window.location.reload();
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch STEP file (${response.status})`);
        }

        const stepContent = await response.text();
        console.log('[ReplicadViewer] STEP file fetched, size:', stepContent.length);
        setLoadingStatus('Parsing CAD geometry...');

        workerRef.current.postMessage({
          type: 'loadSTEP',
          data: { stepContent }
        });
      } catch (err) {
        console.error('Error loading STEP:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadSTEP();
  }, [stepUrl]);

  // Render geometry from worker
  const renderGeometry = (faces, edges) => {
    if (!sceneRef.current) return;

    // Clear previous geometry
    if (meshGroupRef.current) {
      sceneRef.current.remove(meshGroupRef.current);
      meshGroupRef.current.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }

    const group = new THREE.Group();

    // Create faces mesh
    if (faces && faces.length > 0) {
      const geometries = faces.map((face) => {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(face.vertices, 3));
        geometry.setAttribute('normal', new THREE.Float32BufferAttribute(face.normals, 3));
        if (face.triangles) {
          geometry.setIndex(face.triangles);
        }
        return geometry;
      });

      const mergedGeometry = BufferGeometryUtils.mergeGeometries(geometries, false);

      const material = new THREE.MeshPhysicalMaterial({
        color: 0x0071e3,
        metalness: 0.1,
        roughness: 0.4,
        clearcoat: 0.3,
        clearcoatRoughness: 0.25,
        side: THREE.DoubleSide
      });

      const mesh = new THREE.Mesh(mergedGeometry, material);
      group.add(mesh);
    }

    // Create edge lines
    if (edges && edges.length > 0) {
      const edgeMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });

      edges.forEach((edge) => {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(edge.vertices, 3));
        const line = new THREE.Line(geometry, edgeMaterial);
        group.add(line);
      });
    }

    sceneRef.current.add(group);
    meshGroupRef.current = group;

    if (controlsRef.current) {
      centerAndFitModel(group, controlsRef.current);
    }
  };

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#e74c3c' }}>
        <p>Failed to load CAD model</p>
        <p style={{ fontSize: '0.9em', color: '#999' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      {loading && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.9)', padding: '20px 40px',
          borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, color: '#333', fontWeight: 500 }}>Loading CAD model</p>
          <p style={{ margin: '8px 0 0', color: '#666', fontSize: '0.9em' }}>{loadingStatus}</p>
        </div>
      )}
    </div>
  );
}

export default ReplicadViewer;
